<template>
  <div class="iconn_chip" :class="chipClasses" @click="handleClick">
    <Avatar v-if="avatar" :pathImg="avatarImageUrl" size="xs" />

    <slot name="left">
      <IconMaterial v-if="iconLeft" :icon="iconLeft" :size="iconSize" :color="iconColor" :variant="iconLeftVariant" />
    </slot>
    <slot>
      <div v-if="text" class="text-truncate">
        {{ text }}
      </div>
    </slot>
    <slot name="right">
      <IconMaterial v-if="iconRight" :icon="iconRight" :size="iconSize" :color="iconColor" :variant="iconRightVariant"  />
    </slot>
    <IconMaterial
      v-if="close"
      class="cursor-pointer"
      icon="highlight_off"
      variant="outlined"
      :size="iconSize"
      :color="iconColor"
      @click.stop="handleClose"
    />
  </div>
</template>

<script setup lang="ts">
import IconMaterial from '@design_system_v3/components/atoms/IconMaterial.vue';
import Avatar from '@design_system_v3/components/atoms/Avatar.vue';
import { computed, defineProps, withDefaults, defineEmits } from 'vue';

export type Variant = 'primary' | 'secondary' | 'tertiary' | 'success' | 'error' | 'warning';

interface ChipProps {
  text?: string;
  size?: 'sm' | 'md';
  variant?: Variant;
  tone?: 'light' | 'dark';
  avatarImageUrl?: string;
  avatar?: boolean;
  iconLeft?: string;
  iconLeftVariant?: string;
  iconRight?: string;
  iconRightVariant?: string;
  disabled?: boolean;
  close?: boolean;
}
const props = withDefaults(defineProps<ChipProps>(), {
  variant: 'primary',
  tone: 'light',
  size: 'sm',
  disabled: false,
});

const chipClasses = computed(() => {
  return `iconn_chip--${props.disabled ? 'disabled' : props.variant}-${props.tone} iconn_chip--${props.size} `;
});

const iconColor = computed(() => {
  if (props.tone === 'light') {
    if (props.disabled) return '#6F747C';
    if (props.variant === 'primary') return '#602773';
    if (props.variant === 'secondary') return '#2B3F7B';
    if (props.variant === 'tertiary') return '#334155';
    if (props.variant === 'success') return '#0D5947';
    if (props.variant === 'error') return '#962B3F';
    if (props.variant === 'warning') return '#845A09';
  }

  if (props.tone === 'dark') {
    if (props.disabled) return '#6F747C';
    return '#ffffff';
  }
  return '';
});

const iconSize = computed(() => {
  if (props.size === 'sm') return 16;
  if (props.size === 'md') return 20;
  return 16;
});

const emit = defineEmits(['click', 'close']);

const handleClick = (event: MouseEvent) => {
  if (!props.disabled) {
    emit('click', event);
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss">
@import '@foundation';

.iconn_chip {
  display: flex;
  gap: 4px;
  align-items: center;
  border-radius: 24px;
  padding: 4px 8px;
  width: fit-content;
  cursor: default;

  &--sm {
    @extend .height_md;
    @extend .medium-caption-md;
  }

  &--md {
    @extend .height_lg;
    @extend .medium-body-sm;
  }

  &--primary {
    &-light {
      background-color: map-get($primary, 'purple-50');
      color: map-get($primary, 'purple-600');
    }

    &-dark {
      background-color: map-get($primary, 'purple-700');
      color: map-get($basic, 'white');
    }
  }

  &--secondary {
    &-light {
      background-color: map-get($secondary, 'blue-100');
      color: map-get($secondary, 'blue-800');
    }

    &-dark {
      background-color: map-get($secondary, 'blue-800');
      color: map-get($basic, 'white');
    }
  }

  &--tertiary {
    &-light {
      background-color: map-get($neutral, 'neutral-200');
      color: map-get($neutral, 'neutral-700');
    }

    &-dark {
      background-color: map-get($neutral, 'neutral-800');
      color: map-get($basic, 'white');
    }
  }

  &--success {
    &-light {
      background-color: map-get($success, 'green-100');
      color: map-get($success, 'green-900');
    }

    &-dark {
      background-color: map-get($success, 'green-900');
      color: map-get($basic, 'white');
    }
  }

  &--error {
    &-light {
      background-color: map-get($error, 'red-100');
      color: map-get($error, 'red-700');
    }

    &-dark {
      background-color: map-get($error, 'red-800');
      color: map-get($basic, 'white');
    }
  }

  &--warning {
    &-light {
      background-color: map-get($warning, 'orange-100');
      color: map-get($warning, 'orange-800');
    }

    &-dark {
      background-color: map-get($warning, 'orange-900');
      color: map-get($basic, 'white');
    }
  }

  &--disabled {
    &-light {
      cursor: not-allowed;
      background-color: map-get($grey_scale, 'grey-50');
      color: map-get($grey_scale, 'grey-700');
    }

    &-dark {
      cursor: not-allowed;
      background-color: map-get($grey_scale, 'grey-300');
      color: map-get($grey_scale, 'grey-700');
    }
  }
}
</style>
